import { useAccount, useBalance, useConnect, useDisconnect, useSwitchChain } from "wagmi";

import { TokenMark } from "@/components/Brand";
import { Status } from "@/components/primitives";
import { formatASQ, shortAddress } from "@/lib/format";
import { runtimeConfig } from "@/lib/runtime-config";
import { activeChain } from "@/lib/wagmi";

/**
 * Every wallet state from plan §9, and no state that is not one of them.
 *
 * The ordering below is the priority order, and it matters: a wrong-network
 * wallet has a balance the app could read and must not show, because a balance
 * from the wrong chain is a number that will not be there when the user tries
 * to spend it.
 */
export function WalletButton() {
  const { address, isConnected, chainId } = useAccount();
  const { connect, connectors, isPending: connecting, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: switching } = useSwitchChain();

  const config = runtimeConfig();
  const { data: balance } = useBalance({
    address,
    token: config.asqToken,
    query: { enabled: isConnected && chainId === activeChain.id },
  });

  if (!isConnected) {
    const injected = connectors[0];
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          className="btn btn--primary"
          disabled={connecting || !injected}
          onClick={() => injected && connect({ connector: injected })}
        >
          {connecting ? "Menghubungkan…" : "Hubungkan Wallet"}
        </button>
        {!injected ? (
          <span className="micro text-warn">Tidak ada wallet browser terdeteksi</span>
        ) : null}
        {connectError ? (
          <span className="micro text-danger max-w-56 text-right normal-case tracking-normal">
            {connectError.message}
          </span>
        ) : null}
      </div>
    );
  }

  if (chainId !== activeChain.id) {
    return (
      <div className="flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <Status state="bad">Jaringan salah</Status>
          <p className="micro mt-1">Butuh {config.networkName}</p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={switching}
          onClick={() => switchChain({ chainId: activeChain.id })}
        >
          {switching ? "Beralih…" : "Ganti Jaringan"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className="text-right">
        <p className="datum text-xs text-paper-100">{shortAddress(address)}</p>
        <p className="flex items-center justify-end gap-1.5">
          <TokenMark size={13} />
          <span className="datum text-xs text-asq-gold">
            {balance ? formatASQ(balance.value, { maxFractionDigits: 2 }) : "—"} ASQ
          </span>
        </p>
      </div>
      <button
        type="button"
        className="btn btn--ghost !px-3 !text-[0.6875rem]"
        onClick={() => disconnect()}
      >
        Putus
      </button>
    </div>
  );
}
